import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { validateUser, setAuthUser } from "../redux/authSlice";
import { NAVIGATION_SOURCE } from "../utils/constants/navigationConstants";
import { checkRoutePermission } from "../utils/checkRoutePermission";
import { FORBIDDEN, LOGIN, PARTNER_DASHBOARD } from "./paths";
import { getUserPermission } from "../redux/roleManagementSlice";
import {
  hasRole,
  ROLE_REFERRAL_PARTNER,
  ROLE_CLIENT
} from "../utils/accountRoles";

export default function ProtectedRoute() {
  const { authUser, authUserloading } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const source = location.state?.source;

  const { permissions, isfullAccess, permissionsLoading, permissionsFetched } =
    useSelector((state) => state.roleManagement);

  useEffect(() => {
    dispatch(validateUser());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getUserPermission());
  }, [dispatch]);

  useEffect(() => {
    const channel = new BroadcastChannel("auth");
    channel.onmessage = (event) => {
      if (event.data === "logout") {
        dispatch(setAuthUser(false));
      }
    };

    return () => channel.close();
  }, [dispatch]);

  if (authUserloading || permissionsLoading || !permissionsFetched) {
    return null;
  }

  if (authUser === false && source !== NAVIGATION_SOURCE.SIGNUP) {
    return <Navigate to={LOGIN} replace />;
  }

  // A referral partner is not a FundTap customer, so keep a partner-only account
  // out of the customer app: it must not see the customer experience or trigger
  // customer-side records. Admin routes fall through to the permission check
  // below (a partner has no admin access, so it lands on Forbidden). Genuine dual
  // accounts hold the client role and are unaffected; when no roles are known yet
  // this is false, so it fails open to the existing behaviour.
  // Only gate a genuinely authenticated session. During signup (authUser is still
  // false, allowed through above via the SIGNUP source) the cached role names may
  // belong to a previous session on this browser, so partner gating must not apply.
  const isPartnerOnly =
    authUser && hasRole(ROLE_REFERRAL_PARTNER) && !hasRole(ROLE_CLIENT);
  const onPartnerRoute = location.pathname.startsWith("/partner/");
  const onAdminRoute = location.pathname.startsWith("/admin");
  if (isPartnerOnly && !onPartnerRoute && !onAdminRoute) {
    return <Navigate to={PARTNER_DASHBOARD} replace />;
  }

  const hasPermission = checkRoutePermission(
    location.pathname,
    permissions,
    isfullAccess
  );

  if (!hasPermission) {
    return <Navigate to={FORBIDDEN} replace />;
  }

  return <Outlet />;
}
